
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Route, Routes, useNavigate } from 'react-router-dom';
import {
  Container,
  CssBaseline,
  createTheme,
  ThemeProvider,
} from '@mui/material';

import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ComingSoonPage from './pages/ComingSoonPage';

import CreateRacePage from './pages/CreateRacePage';
import UserListPage from './pages/UserListPage';
import UploadImage from './pages/UploadImage';
import RaceListPage from './pages/RaceListPage';
import RaceImagesPage from './pages/RaceImagesPage';
import ManageUserPage from './pages/ManageUserPage';
import EditRacePage from './pages/EditRacePage';
import RaceSearchPage from './pages/RaceSearchPage';
import HomePage from './pages/HomePage';
import CartPage from './pages/CartPage';
import TempCheckoutPage from './pages/TempCheckoutPage';
import SuccessPage from './pages/SuccessPage';
import DashboardPage from './pages/DashboardPage';


import GenerateDirectorTokenPage from './pages/GenerateDirectorTokenPage';
import GenerateToken from './pages/GenerateToken';
import GeneratePhotographerTokenPage from './pages/GeneratePhotographerTokenPage';
import DirectorOrAdminRoute from './components/DirectorOrAdminRoute';
import AdminRoute from './components/AdminRoute';
import PhotographerOrDirectorOrAdminRoute from './components/PhotographerOrDirectorOrAdminRoute';
import LoggedInRoute from './components/LoggedInRoute';
import Navbar from './components/Navbar';
import './App.css';

axios.defaults.withCredentials = true;

const theme = createTheme();

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);
  const navigate = useNavigate();

  const checkLoginStatus = async () => {
    try {
      const response = await axios.get('/api/users/status');
      setIsLoggedIn(response.data.loggedIn);
      setUser(response.data.user || null);
      setLoading(false);
    } catch (error) {
      console.error('Error checking login status:', error);
      setIsLoggedIn(false);
      setUser(null);
    }
  };

  useEffect(() => {
    checkLoginStatus();
  }, []);

  const handleLoginSuccess = async () => {
    await checkLoginStatus();
    navigate('/dashboard');
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/users/logout');
      setIsLoggedIn(false);
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const addToCart = (image, raceName) => {
    if (cart.find((item) => item.id === image.id)) {
      // Already in cart
    } else {
      setCart([...cart, { ...image, price: 4.99, raceName }]);
    }
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  };

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const totalCost = cart.reduce((total, item) => total + item.price, 0);

  const handleTempCheckout = () => {
    navigate('/temp-checkout', { state: { cart: cart } });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <Navbar cartItemCount={cart.length} isLoggedIn={isLoggedIn} user={user} handleLogout={handleLogout} />

        <main>
          <Container style={{ marginTop: '2rem' }}>
            <Routes>
              <Route path="/" element={<ComingSoonPage />} />
              <Route path="/beta" element={<HomePage />} />
              <Route path="/login" element={<LoginPage onLoginSuccess={handleLoginSuccess} />} />
              <Route path="/register" element={<RegisterPage setIsLoggedIn={setIsLoggedIn} />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              
              <Route path="/create-race" element={<CreateRacePage />} />
              <Route path="/users" element={<AdminRoute user={user}><UserListPage /></AdminRoute>} />
              <Route path="/upload-image/:raceId" element={<UploadImage />} />
              <Route path="/races" element={<PhotographerOrDirectorOrAdminRoute user={user}><RaceListPage /></PhotographerOrDirectorOrAdminRoute>} />
              <Route path="/races/:raceId/images" element={<RaceImagesPage addToCart={addToCart} cart={cart} />} />
              <Route path="/users/:userId/manage" element={<ManageUserPage />} />
              <Route path="/edit-race/:raceId" element={<EditRacePage />} />
              <Route path="/race-search" element={<RaceSearchPage />} />
              <Route path="/temp-checkout" element={<TempCheckoutPage />} />
              <Route path="/success" element={<SuccessPage clearCart={clearCart} />} />
              <Route path="/cart" element={<CartPage cart={cart} totalCost={totalCost} removeFromCart={removeFromCart} clearCart={clearCart} handleCheckout={handleTempCheckout} />} />
              <Route path="/dashboard" element={<LoggedInRoute user={user}><DashboardPage /></LoggedInRoute>} />
              
              <Route path="/generate-director-token" element={<AdminRoute user={user}><GenerateDirectorTokenPage /></AdminRoute>} />
              <Route path="/generate-token" element={<DirectorOrAdminRoute user={user}><GenerateToken /></DirectorOrAdminRoute>} />
              <Route path="/generate-photographer-token" element={<DirectorOrAdminRoute user={user}><GeneratePhotographerTokenPage /></DirectorOrAdminRoute>} />
            </Routes>
          </Container>
        </main>
      </div>
    </ThemeProvider>
  );
}

export default App;
